import React from "react";
import { Space } from "antd";
import { AxNotification, AxButton, AxDivider } from "@dynata/axiom3";

class Notification extends React.Component {
  render() {
    return (
      <div className="section" id="notification">
        <h3>Notification</h3>
        <p>Display a notification message globally.</p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{`<AxButton
  onClick={() =>
  AxNotification.show({
    btn: (
      <div>
        <AxButton color="blue" size="small">
          Text
        </AxButton>
      </div>
    ),
    duration: 3,
    placement: "bottomLeft",
    description: "Description text",
    closeIcon: <i className="fa-solid fa-xmark"></i>,
  })
}
>
  Show Notification
</AxButton>`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/notication/#API"
                >
                  https://ant.design/components/notication/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>type</td>
                  <td>Default is, normal</td>
                  <td>info | success | warning | error | normal</td>
                  <td></td>
                </tr>
                <tr>
                  <td>icon</td>
                  <td>Status icon</td>
                  <td>ReactNode</td>
                  <td></td>
                </tr>
                <tr>
                  <td>closeIcon</td>
                  <td>Close icon</td>
                  <td>ReactNode</td>
                  <td></td>
                </tr>

                <tr>
                  <td>btn</td>
                  <td>Customized button in the footer</td>
                  <td>ReactNode</td>
                  <td></td>
                </tr>
                <tr>
                  <td>placement</td>
                  <td>Position of Notification</td>
                  <td>"topLeft | topRight | bottomLeft | bottomRight"</td>
                  <td></td>
                </tr>
                <tr>
                  <td>description</td>
                  <td>Notification content</td>
                  <td>string | ReactNode</td>
                  <td></td>
                </tr>
                <tr>
                  <td>duration</td>
                  <td>
                    Time (seconds) before auto-dismiss, don't dismiss if set to
                    0
                  </td>
                  <td>number</td>
                  <td></td>
                </tr>
                <tr>
                  <td>bottom</td>
                  <td>
                    Distance from the bottom of the viewport, when{" "}
                    <code>placement</code> is <code>bottomRight</code> or{" "}
                    <code>bottomLeft</code> (unit: pixels)
                  </td>
                  <td>number</td>
                  <td>24</td>
                </tr>
                <tr>
                  <td>btn</td>
                  <td>Customized close button</td>
                  <td>ReactNode</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>className</td>
                  <td>Customized CSS class</td>
                  <td>string</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>closeIcon</td>
                  <td>Custom close icon</td>
                  <td>ReactNode</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>description</td>
                  <td>The content of notification box (required)</td>
                  <td>ReactNode</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>duration</td>
                  <td>
                    Time in seconds before Notification is closed. When set to 0
                    or null, it will never be closed automatically
                  </td>
                  <td>number</td>
                  <td>4.5</td>
                </tr>
                <tr>
                  <td>getContainer</td>
                  <td>Return the mount node for Notification</td>
                  <td>() =&gt; HTMLNode</td>
                  <td>() =&gt; document.body</td>
                </tr>
                <tr>
                  <td>icon</td>
                  <td>Customized icon</td>
                  <td>ReactNode</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>key</td>
                  <td>The unique identifier of the Notification</td>
                  <td>string</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>message</td>
                  <td>The title of notification box (required)</td>
                  <td>ReactNode</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>placement</td>
                  <td>
                    Position of Notification, can be one of <code>topLeft</code>{" "}
                    <code>topRight</code> <code>bottomLeft</code>{" "}
                    <code>bottomRight</code>
                  </td>
                  <td>string</td>
                  <td>
                    <code>topRight</code>
                  </td>
                </tr>
                <tr>
                  <td>style</td>
                  <td>Customized inline style</td>
                  <td>
                    <a href="https://github.com/DefinitelyTyped/DefinitelyTyped/blob/e434515761b36830c3e58a970abf5186f005adac/types/react/index.d.ts#L794">
                      CSSProperties
                    </a>
                  </td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>top</td>
                  <td>
                    Distance from the top of the viewport, when{" "}
                    <code>placement</code> is <code>topRight</code> or{" "}
                    <code>topLeft</code> (unit: pixels)
                  </td>
                  <td>number</td>
                  <td>24</td>
                </tr>
                <tr>
                  <td>onClick</td>
                  <td>
                    Specify a function that will be called when the notification
                    is clicked
                  </td>
                  <td>function</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>onClose</td>
                  <td>Trigger when notification closed</td>
                  <td>function</td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>

            <pre>{`
<AxButton
  onClick={() =>
    AxNotification.show({
      type: "info",
      btn: (
        <div>
          <AxButton size="small">Button</AxButton>
        </div>
      ),
      duration: 0,
      placement: "bottomLeft",
      description: "Search for the keywords to learn more about each warning.",
      icon: <i className="fa-regular fa-circle-info"></i>,
      closeIcon: <i className="fa-solid fa-xmark"></i>,
    })
  }
>
  Info
</AxButton>

<AxButton
  onClick={() =>
    AxNotification.show({
      type: "success",
      btn: (
        <div>
          <AxButton color="blue" size="small">
            Text
          </AxButton>
        </div>
      ),
      duration: 0,
      placement: "bottomLeft",
      description: "Search for the keywords to learn more about each warning.",
      icon: <i className="fa-regular fa-circle-check"></i>,
      closeIcon: <i className="fa-solid fa-xmark"></i>,
    })
  }
>
  Success
</AxButton>

<AxButton
  onClick={() =>
    AxNotification.show({
      type: "warning",
      btn: (
        <div>
          <AxButton color="blue" size="small">
            Text
          </AxButton>
        </div>
      ),
      duration: 1.5,
      placement: "bottomLeft",
      description: "Search for the keywords to learn more about each warning.",
      icon: <i className="fa-regular fa-circle-exclamation"></i>,
      closeIcon: <i className="fa-solid fa-xmark"></i>,
    })
  }
>
  Warning
</AxButton>

<AxButton
  onClick={() =>
    AxNotification.show({
      type: "error",
      btn: (
        <div>
          <AxButton color="blue" size="small">
            Text
          </AxButton>
        </div>
      ),
      duration: 1.5,
      placement: "bottomLeft",
      description: "Search for the keywords to learn more about each warning.",
      icon: <i className="fa-regular fa-triangle-exclamation"></i>,
      closeIcon: <i className="fa-solid fa-xmark"></i>,
    })
  }
>
  Error
</AxButton>

<AxButton
  onClick={() =>
    AxNotification.show({
      btn: (
        <div>
          <AxButton color="blue" size="small">
            Text
          </AxButton>
        </div>
      ),
      duration: 3,
      placement: "bottomLeft",
      description: "Search for the keywords to learn more about each warning.",
      closeIcon: <i className="fa-solid fa-xmark"></i>,
    })
  }
>
  Normal
</AxButton>
`}</pre>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Info | Success | Warning | Error | Normal
        </AxDivider>

        <Space>
          <AxButton
            onClick={() =>
              AxNotification.show({
                type: "info",
                size: "short",
                btn: (
                  <div>
                    <AxButton size="small">Button</AxButton>
                  </div>
                ),
                duration: 0,
                placement: "bottomLeft",
                description:
                  "Search for the keywords to learn more about each warning.",
                icon: <i className="fa-regular fa-circle-info"></i>,
                closeIcon: <i className="fa-solid fa-xmark"></i>,
              })
            }
          >
            Info
          </AxButton>

          <AxButton
            onClick={() =>
              AxNotification.show({
                type: "success",
                btn: (
                  <div>
                    <AxButton color="blue" size="small">
                      Text
                    </AxButton>
                  </div>
                ),
                duration: 0,
                placement: "bottomLeft",
                description:
                  "Search for the keywords to learn more about each warning.",
                icon: <i className="fa-regular fa-circle-check"></i>,
                closeIcon: <i className="fa-solid fa-xmark"></i>,
              })
            }
          >
            Success
          </AxButton>

          <AxButton
            onClick={() =>
              AxNotification.show({
                type: "warning",
                btn: (
                  <div>
                    <AxButton color="blue" size="small">
                      Text
                    </AxButton>
                  </div>
                ),
                duration: 1.5,
                placement: "bottomLeft",
                description:
                  "Search for the keywords to learn more about each warning.",
                icon: <i className="fa-regular fa-circle-exclamation"></i>,
                closeIcon: <i className="fa-solid fa-xmark"></i>,
              })
            }
          >
            Warning
          </AxButton>

          <AxButton
            onClick={() =>
              AxNotification.show({
                type: "error",
                btn: (
                  <div>
                    <AxButton color="blue" size="small">
                      Text
                    </AxButton>
                  </div>
                ),
                duration: 1.5,
                placement: "bottomLeft",
                description:
                  "Search for the keywords to learn more about each warning.",
                icon: <i className="fa-regular fa-triangle-exclamation"></i>,
                closeIcon: <i className="fa-solid fa-xmark"></i>,
              })
            }
          >
            Error
          </AxButton>

          <AxButton
            onClick={() =>
              AxNotification.show({
                btn: (
                  <div>
                    <AxButton color="blue" size="small">
                      Text
                    </AxButton>
                  </div>
                ),
                duration: 3,
                placement: "bottomLeft",
                description:
                  "Search for the keywords to learn more about each warning.",
                closeIcon: <i className="fa-solid fa-xmark"></i>,
              })
            }
          >
            Normal
          </AxButton>
        </Space>
      </div>
    );
  }
}
export default Notification;
